/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import { useCallback, useRef, useState } from "react";

/**
 * One request at a time, and a screen that says so.
 *
 * Every page here submits by clicking a `<div className="button">`, which has no
 * disabled state of its own — so a second click during a request simply sends the
 * request again. That is not theoretical: an SMS code screen that takes two
 * seconds to answer collects as many sends as the user has patience for, and each
 * one counts against their send budget.
 *
 * The guard is a ref **and** a state on purpose. The ref rejects a second click
 * within the same tick, before React has re-rendered; the state is what greys the
 * button out and shows the spinner. One of them alone leaves a gap — the state
 * lags the click, and the ref is invisible.
 *
 * Returns:
 *   busy       – true while a request is in flight
 *   run(fn)    – runs `fn` under the guard, releasing it when it settles;
 *                returns undefined (and does not call `fn`) on a blocked click
 *   begin/end  – the same guard, for a flow that cannot be expressed as one call
 *   buttonProps(extra) – className/style/aria for a submitting button
 */
export function useSubmitGuard() {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const begin = useCallback(() => {
    if (inFlight.current) {
      return false;
    }
    inFlight.current = true;
    setBusy(true);
    return true;
  }, []);

  const end = useCallback(() => {
    inFlight.current = false;
    setBusy(false);
  }, []);

  /**
   * `finally` and not a `then`: a request that throws must release the guard too,
   * or the screen stays locked on a failure the user could have retried.
   */
  const run = useCallback(
    async (action) => {
      if (!begin()) {
        return undefined;
      }
      try {
        return await action();
      } finally {
        end();
      }
    },
    [begin, end]
  );

  /**
   * What a submitting control needs, in one spread.
   *
   * `base` is the button's own class list, so the caller never has to merge two
   * `className` props and hope the later one wins. `blocked` folds in the other
   * reasons a control is unavailable — a rate-limit countdown, an empty field —
   * so a button has one source of "not now" instead of two that can disagree.
   */
  const buttonProps = useCallback(
    ({ base = "", blocked = false } = {}) => {
      const disabled = busy || blocked;
      return {
        className: `${base}${disabled ? " is-busy" : ""}`,
        "aria-busy": busy ? "true" : undefined,
        "aria-disabled": disabled ? "true" : undefined,
        style: disabled ? { opacity: 0.55, pointerEvents: "none" } : undefined,
      };
    },
    [busy]
  );

  return { busy, begin, end, run, buttonProps };
}
